
import { useCallback } from 'react';

interface CalculatorSliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  icon: string;
  suffix: string;
  prefix?: string;
  onChange: (value: number) => void;
  color?: string;
}

export default function CalculatorSlider({
  label,
  value,
  min,
  max,
  step = 1,
  icon,
  suffix,
  prefix = '',
  onChange,
  color = 'amber',
}: CalculatorSliderProps) {
  const percentage = ((value - min) / (max - min)) * 100;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(Number(e.target.value));
    },
    [onChange],
  );

  const colorMap: Record<string, { bg: string; text: string; track: string }> = {
    amber: { bg: 'bg-amber-100', text: 'text-amber-600', track: 'accent-amber-500' },
    emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600', track: 'accent-emerald-500' },
    orange: { bg: 'bg-orange-100', text: 'text-orange-600', track: 'accent-orange-500' },
  };

  const c = colorMap[color] || colorMap.amber;

  return (
    <div className="group">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <div className={`w-8 h-8 ${c.bg} rounded-lg flex items-center justify-center`}>
            <i className={`${icon} ${c.text} text-base`}></i>
          </div>
          <label className="text-slate-700 font-semibold text-sm">{label}</label>
        </div>
        <div className={`${c.text} font-bold text-lg tabular-nums`}>
          {prefix}{value.toLocaleString()}{suffix && <span className="text-xs font-medium text-slate-400 ml-1">{suffix}</span>}
        </div>
      </div>

      <div className="relative">
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full transition-all duration-150"
            style={{ width: `${percentage}%` }}
          />
        </div>
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={handleChange}
          className={`absolute inset-0 w-full h-2 opacity-0 cursor-pointer`}
        />
      </div>

      <div className="flex justify-between text-[11px] text-slate-400 mt-1.5 font-medium">
        <span>{prefix}{min.toLocaleString()}</span>
        <span>{prefix}{max.toLocaleString()}</span>
      </div>
    </div>
  );
}
